import { ApiError, type PortalThreadWatchers, type ThreadWatcher } from '@central/api';
import { color } from '@central/ui';
import { Alert, Button, MultiSelect, Select } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, meQuery, teamQuery, watchersQuery } from '../api';
import { errorMessage } from '../format';

/**
 * Who gets emails about a conversation. With no one added, everyone on the
 * team with conversation emails on; once someone is added, only they and
 * the reporter. Owners and Members add and remove anyone on the team; a
 * Viewer only watches or stops watching themselves.
 */
export function PeopleNotified({ threadId }: { threadId: string }) {
  const qc = useQueryClient();
  const me = useQuery(meQuery).data?.user;
  const viewer = me?.role === 'Viewer';
  const w = useQuery(watchersQuery(threadId));
  const team = useQuery({ ...teamQuery, enabled: !!me && !viewer });
  const change = useMutation({
    mutationFn: ({ userId, on }: { userId: string; on: boolean }) =>
      on ? api.threads.watch(threadId, userId) : api.threads.unwatch(threadId, userId),
    onSuccess: (d) => qc.setQueryData<PortalThreadWatchers>(watchersQuery(threadId).queryKey, d),
  });

  if (w.error) {
    return (
      <p style={{ margin: 0, fontSize: 13, color: color.textMuted }}>
        Couldn’t load who is notified about this conversation.
      </p>
    );
  }
  const data = w.data;
  if (!data || !me) return null;
  const listed = new Set(data.watchers.map((x) => x.id));
  const reporterIsMe = data.reporter?.id === me.id;
  const candidates = (team.data?.users ?? []).filter(
    (u) => u.enabled && !listed.has(u.id) && u.id !== data.reporter?.id,
  );

  return (
    <section
      aria-label="People notified"
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      {!reporterIsMe && (
        <Button
          variant="default"
          h={40}
          style={{ alignSelf: 'flex-start' }}
          loading={change.isPending}
          onClick={() => change.mutate({ userId: 'me', on: !data.watching })}
        >
          {data.watching ? 'Stop watching' : 'Watch'}
        </Button>
      )}
      {data.everyone ? (
        <p style={{ margin: 0, fontSize: 13, color: color.textMuted }}>
          Everyone on your team with conversation emails on. Adding people sends emails only to them
          and the person who reported it.
        </p>
      ) : (
        <ul
          aria-label="Watchers"
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}
        >
          {data.reporter && <Person p={data.reporter} note="reported it" />}
          {data.watchers
            .filter((p) => p.id !== data.reporter?.id)
            .map((p) => (
              <Person
                key={p.id}
                p={p}
                onRemove={
                  !viewer || p.id === me.id
                    ? () => change.mutate({ userId: p.id, on: false })
                    : undefined
                }
              />
            ))}
        </ul>
      )}
      {reporterIsMe && (
        <p style={{ margin: 0, fontSize: 12, color: color.textMuted }}>
          You reported this conversation, so you’re always notified while your conversation emails
          are on.
        </p>
      )}
      {!viewer && candidates.length > 0 && (
        <Select
          aria-label="Add a teammate"
          placeholder="Add a teammate"
          size="sm"
          searchable
          value={null}
          data={candidates.map((u) => ({ value: u.id, label: `${u.name} (${u.email})` }))}
          onChange={(v) => v && change.mutate({ userId: v, on: true })}
        />
      )}
      {change.error && <Alert color="red">{errorMessage(change.error)}</Alert>}
    </section>
  );
}

function Person({ p, note, onRemove }: { p: ThreadWatcher; note?: string; onRemove?: () => void }) {
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
      <span>
        {p.name}
        {note && <span style={{ color: color.textMuted }}> · {note}</span>}
        {!p.receivesEmail && <span style={{ color: color.textMuted }}> · emails off</span>}
      </span>
      {onRemove && (
        <Button
          size="xs"
          h={40}
          variant="subtle"
          aria-label={`Remove ${p.name}`}
          onClick={onRemove}
        >
          Remove
        </Button>
      )}
    </li>
  );
}

/**
 * Teammates to copy on a new conversation. Hidden for a Viewer, who may
 * copy only themselves and is notified anyway as its reporter.
 */
export function CcPicker({
  value,
  onChange,
  error,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  error?: unknown;
}) {
  const me = useQuery(meQuery).data?.user;
  const viewer = !me || me.role === 'Viewer';
  const team = useQuery({ ...teamQuery, enabled: !viewer });
  if (viewer) return null;
  const users = (team.data?.users ?? []).filter((u) => u.enabled && u.id !== me.id);
  if (users.length === 0) return null;
  return (
    <MultiSelect
      label="Notify teammates"
      description="Only you and the people you pick get emails about it. Pick no one to notify everyone with conversation emails on."
      placeholder={value.length ? undefined : 'Everyone'}
      searchable
      data={users.map((u) => ({ value: u.id, label: `${u.name} (${u.email})` }))}
      value={value}
      onChange={onChange}
      error={
        error instanceof ApiError && error.fields?.cc
          ? 'Someone you picked is no longer on your team.'
          : undefined
      }
    />
  );
}
